import { useState } from 'react';
import { Check, MessageSquare, ChevronDown, ChevronUp, Calendar, Send, X, ExternalLink } from 'lucide-react';
import type { ClientReviewData, ClientReviewPost } from '../services/clientService';
import { chClientReviewService } from '../services/clientService';
import { PLATFORM_LABELS, CONTENT_TYPE_LABELS } from '../types';
import { MediaPreview } from './MediaPreview';
import { MediaCarousel } from './MediaCarousel';
import { getCoverUrl, getMediaUrls, getMetricsItem, getPlatforms } from '../lib/posts';
import { PrintsGallery } from './PrintsGallery';
import { MetricsSummaryStrip, PostMetricsGrid } from './MetricsDisplay';
import { summarizeMetrics, getBestPublishingTime } from '../lib/metrics';
import { MentionTextarea, MentionText } from './MentionTextarea';
import { extractMentionIds, splitMentions, type MentionCandidate } from '../lib/mentions';

function getContrastColor(hex: string): string {
  const c = hex.replace('#', '');
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#111111' : '#ffffff';
}

function getPlatformIcon(platform: string): string {
  const icons: Record<string, string> = {
    instagram: 'IG',
    tiktok: 'TK',
    youtube: 'YT',
    podcast: 'PD',
  };
  return icons[platform] || platform.toUpperCase().slice(0, 2);
}

interface ActionHandlers {
  approve: (postId: string, actorName: string) => Promise<{ success: boolean }>;
  requestChanges: (postId: string, actorName: string, comment: string) => Promise<{ success: boolean }>;
  addComment: (postId: string, actorName: string, comment: string, mentions?: string[]) => Promise<{ success: boolean }>;
  getComments: (postId: string) => Promise<{ success: boolean; comments?: any[] }>;
}

interface Props {
  data: ClientReviewData;
  token: string;
  actorName: string;
  onNeedName: () => void;
  actions?: ActionHandlers;
  viewOnly?: boolean;
  /** Logged-in clients can tag the team; public links can't. */
  mentionCandidates?: MentionCandidate[];
}

interface PostComments {
  [postId: string]: Array<{ author_name: string; text: string; created_at: string; mentions?: string[] | null }>;
}

export function ClientReviewContent({ data, token, actorName, onNeedName, actions, viewOnly, mentionCandidates }: Props) {
  const mentionNames = new Map((mentionCandidates || []).map(c => [c.id, c.name]));
  const [posts, setPosts] = useState(data.posts || []);
  const [expandedCaption, setExpandedCaption] = useState<string | null>(null);
  const [changesDialog, setChangesDialog] = useState<string | null>(null);
  const [changesComment, setChangesComment] = useState('');
  const [commentDialog, setCommentDialog] = useState<string | null>(null);
  const [commentText, setCommentText] = useState('');
  const [commentsOpen, setCommentsOpen] = useState<string | null>(null);
  const [postComments, setPostComments] = useState<PostComments>({});
  const [submitting, setSubmitting] = useState(false);

  const branding = data.branding;
  const primaryColor = branding?.primary_color || '#2563eb';
  const bgColor = branding?.background_color || '#ffffff';
  const accentColor = branding?.accent_color || '#6b7280';
  const textColor = getContrastColor(bgColor);
  const textMuted = textColor === '#ffffff' ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.5)';
  const primaryTextColor = getContrastColor(primaryColor);

  function ensureName(): boolean {
    if (!actorName) {
      onNeedName();
      return false;
    }
    return true;
  }

  async function handleApprove(postId: string) {
    if (!ensureName()) return;
    setSubmitting(true);
    const result = actions
      ? await actions.approve(postId, actorName)
      : await chClientReviewService.approvePost(token, postId, actorName);
    if (result.success) {
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, client_decision: 'approved' } : p));
    }
    setSubmitting(false);
  }

  async function handleRequestChanges() {
    if (!changesDialog || !changesComment.trim()) return;
    if (!ensureName()) return;
    setSubmitting(true);
    const result = actions
      ? await actions.requestChanges(changesDialog, actorName, changesComment.trim())
      : await chClientReviewService.requestChanges(token, changesDialog, actorName, changesComment.trim());
    if (result.success) {
      setPosts(prev => prev.map(p => p.id === changesDialog ? { ...p, client_decision: 'changes_requested' } : p));
    }
    setChangesDialog(null);
    setChangesComment('');
    setSubmitting(false);
  }

  async function handleAddComment() {
    if (!commentDialog || !commentText.trim()) return;
    if (!ensureName()) return;
    setSubmitting(true);
    const mentions = mentionCandidates ? extractMentionIds(commentText.trim(), mentionCandidates) : [];
    const result = actions
      ? await actions.addComment(commentDialog, actorName, commentText.trim(), mentions)
      : await chClientReviewService.addComment(token, commentDialog, actorName, commentText.trim());
    if (result.success) {
      setPostComments(prev => ({
        ...prev,
        [commentDialog]: [...(prev[commentDialog] || []), { author_name: actorName, text: commentText.trim(), created_at: new Date().toISOString(), mentions }],
      }));
    }
    setCommentDialog(null);
    setCommentText('');
    setSubmitting(false);
  }

  async function loadComments(postId: string) {
    if (commentsOpen === postId) {
      setCommentsOpen(null);
      return;
    }
    const result = actions
      ? await actions.getComments(postId)
      : await chClientReviewService.getComments(token, postId);
    if (result.success) {
      setPostComments(prev => ({ ...prev, [postId]: result.comments || [] }));
    }
    setCommentsOpen(postId);
  }

  // Published items are the record of work done (with stats and prints);
  // the rest is either waiting for the client or approved and scheduled.
  const publishedPosts = posts.filter(p => p.status === 'publicado');
  const pendingPosts = posts.filter(p => p.status !== 'publicado' && p.client_decision !== 'approved');
  const approvedPosts = posts.filter(p => p.status !== 'publicado' && p.client_decision === 'approved');

  const isDark = textColor === '#ffffff';
  const cardBg = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.95)';
  const cardTitle = isDark ? '#fff' : '#111';
  const tilePalette = { text: cardTitle, muted: textMuted, surface: isDark ? 'rgba(255,255,255,0.06)' : '#f5f5f4' };
  const summary = summarizeMetrics(posts.map(getMetricsItem));
  const bestTime = getBestPublishingTime(posts
    .filter(p => p.status === 'publicado')
    .map(p => ({ published_at: p.published_at, metrics: getMetricsItem(p) })));
  const followers = data.project?.followers;


  function renderPostCard(post: ClientReviewPost, reviewable: boolean) {
    return (
                  <article
                    key={post.id}
                    className="rounded-2xl overflow-hidden shadow-lg backdrop-blur-sm"
                    style={{ backgroundColor: cardBg }}
                  >
                    {/* Media */}
                    {getMediaUrls(post).length > 0 ? (
                      <div className="aspect-video bg-black/10 overflow-hidden">
                        <MediaCarousel
                          urls={getMediaUrls(post)}
                          alt={post.title}
                          poster={post.thumbnail_url}
                          mediaClassName="w-full aspect-video object-cover"
                          accent={primaryColor}
                        />
                      </div>
                    ) : post.thumbnail_url ? (
                      <div className="aspect-video bg-black/10 overflow-hidden">
                        <MediaPreview url={post.thumbnail_url} alt={post.title} className="w-full h-full object-cover" />
                      </div>
                    ) : (
                      <div className="aspect-video flex items-center justify-center" style={{ backgroundColor: accentColor + '20' }}>
                        <span className="text-3xl font-bold opacity-20">{getPlatformIcon(post.platform)}</span>
                      </div>
                    )}
  
                    <div className="p-5">
                      {/* Meta */}
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        {getPlatforms(post).map(p => (
                          <span
                            key={p}
                            className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full"
                            style={{ backgroundColor: primaryColor + '20', color: primaryColor }}
                          >
                            {PLATFORM_LABELS[p]}
                          </span>
                        ))}
                        <span className="text-[10px] font-medium px-2 py-0.5 rounded-full" style={{ backgroundColor: accentColor + '15', color: accentColor }}>
                          {CONTENT_TYPE_LABELS[post.content_type as keyof typeof CONTENT_TYPE_LABELS] || post.content_type}
                        </span>
                        {post.published_at ? (
                          <span className="text-[10px] flex items-center gap-0.5 ml-auto" style={{ color: textMuted }}>
                            <Calendar className="w-3 h-3" />
                            {new Date(post.published_at).toLocaleString('pt-PT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                          </span>
                        ) : post.scheduled_date && (
                          <span className="text-[10px] flex items-center gap-0.5 ml-auto" style={{ color: textMuted }}>
                            <Calendar className="w-3 h-3" />
                            {new Date(post.scheduled_date).toLocaleDateString('pt-PT', { day: '2-digit', month: 'short' })}
                          </span>
                        )}
                      </div>
  
                      <h3 className="font-bold text-base mb-2" style={{ color: cardTitle }}>
                        {post.title}
                      </h3>
  
                      {post.external_link && post.status === 'publicado' && (
                        <a
                          href={post.external_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs font-semibold mb-3 hover:underline"
                          style={{ color: primaryColor }}
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                          Ver publicação
                        </a>
                      )}
  
                      {/* Caption */}
                      {post.caption && (
                        <div className="mb-4">
                          <p
                            className={`text-sm whitespace-pre-wrap ${expandedCaption === post.id ? '' : 'line-clamp-3'}`}
                            style={{ color: textMuted }}
                          >
                            {post.caption}
                          </p>
                          {post.caption.length > 150 && (
                            <button
                              onClick={() => setExpandedCaption(expandedCaption === post.id ? null : post.id)}
                              className="text-xs font-medium mt-1 underline"
                              style={{ color: primaryColor }}
                            >
                              {expandedCaption === post.id ? 'Ver menos' : 'Ver mais'}
                            </button>
                          )}
                        </div>
                      )}
  
                      {/* Changes requested state */}
                      {post.client_decision === 'changes_requested' && (
                        <div className="mb-4 p-3 rounded-xl text-sm font-medium" style={{ backgroundColor: '#fef3c7', color: '#92400e' }}>
                          Alterações pedidas — a equipa está a trabalhar nisto.
                        </div>
                      )}
  
                      {/* Stats and prints */}
                      {(post.metrics || (post.prints && post.prints.length > 0)) && (
                        <div className="mb-4 space-y-3">
                          <PostMetricsGrid metrics={getMetricsItem(post)} byPlatform={post.metrics_by_platform} palette={tilePalette} />
                          {post.prints && post.prints.length > 0 && (
                            <div>
                              <p className="text-[10px] font-bold uppercase tracking-wider mb-1.5" style={{ color: textMuted }}>Prints</p>
                              <PrintsGallery prints={post.prints} thumbClassName="w-16 h-16" />
                            </div>
                          )}
                        </div>
                      )}
  
                      {/* Actions */}
                      {reviewable && !viewOnly && (
                      <div className="flex gap-2 flex-wrap">
                        <button
                          onClick={() => handleApprove(post.id)}
                          disabled={submitting}
                          className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full font-bold text-sm transition-transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50"
                          style={{ backgroundColor: primaryColor, color: primaryTextColor }}
                        >
                          <Check className="w-4 h-4" />
                          Aprovar
                        </button>
                        <button
                          onClick={() => setChangesDialog(post.id)}
                          disabled={submitting}
                          className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full font-semibold text-sm border-2 transition-colors hover:opacity-80"
                          style={{ borderColor: accentColor, color: accentColor }}
                        >
                          Pedir Alterações
                        </button>
                      </div>
                      )}
  
                      {/* Comments toggle */}
                      <button
                        onClick={() => loadComments(post.id)}
                        className="mt-3 flex items-center gap-1.5 text-xs font-medium transition-colors hover:opacity-80"
                        style={{ color: textMuted }}
                      >
                        <MessageSquare className="w-3.5 h-3.5" />
                        Comentários
                        {commentsOpen === post.id ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                      </button>
  
                      {/* Comments section */}
                      {commentsOpen === post.id && (
                        <div className="mt-3 pt-3 border-t" style={{ borderColor: textMuted + '30' }}>
                          {(postComments[post.id] || []).length === 0 ? (
                            <p className="text-xs" style={{ color: textMuted }}>Sem comentários.</p>
                          ) : (
                            <div className="space-y-2 max-h-40 overflow-y-auto mb-3">
                              {(postComments[post.id] || []).map((c, i) => (
                                <div key={i} className="text-xs">
                                  <span className="font-semibold" style={{ color: primaryColor }}>{c.author_name}</span>
                                  <span className="ml-2 whitespace-pre-wrap" style={{ color: textMuted }}>
                                  <MentionText
                                    parts={splitMentions(c.text, (c.mentions || []).map(id => mentionNames.get(id) || ''))}
                                    highlightClassName="font-semibold"
                                    highlightStyle={{ color: primaryColor }}
                                  />
                                </span>
                                </div>
                              ))}
                            </div>
                          )}
                          {!viewOnly && (
                          <button
                            onClick={() => setCommentDialog(post.id)}
                            className="text-xs font-medium underline"
                            style={{ color: primaryColor }}
                          >
                            Adicionar comentário
                          </button>
                          )}
                        </div>
                      )}
                    </div>
                  </article>
    );
  }

  return (
    <div
      className="min-h-screen font-['Inter',system-ui,sans-serif]"
      style={{
        backgroundColor: bgColor,
        backgroundImage: branding?.background_image_url ? `url(${branding.background_image_url})` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        color: textColor,
      }}
    >
      {/* Header */}
      <header className="pt-10 pb-8 px-4 text-center">
        {branding?.logo_url && (
          <img
            src={branding.logo_url}
            alt={data.project?.name}
            className="h-12 mx-auto mb-4 object-contain"
          />
        )}
        <h1 className="text-2xl font-bold tracking-tight" style={{ color: textColor }}>
          {data.project?.name}
        </h1>
        <p className="text-sm mt-1" style={{ color: textMuted }}>
          {viewOnly ? 'Visualização de conteúdo' : 'Revisão de conteúdo'}
        </p>
      </header>

      {/* Posts */}
      <main className="max-w-4xl mx-auto px-4 pb-16">
        {(summary.itemsWithStats > 0 || followers && Object.keys(followers).length > 0) && (
          <section className="mb-10">
            <h2 className="text-xs font-bold uppercase tracking-wider mb-4" style={{ color: textMuted }}>
              Resultados
            </h2>
            <MetricsSummaryStrip
              summary={summary}
              publishedCount={publishedPosts.length}
              followers={followers}
              bestTime={bestTime}
              palette={{ text: cardTitle, muted: textMuted, surface: cardBg }}
            />
          </section>
        )}

        {pendingPosts.length > 0 && (
          <section className="mb-12">
            <h2 className="text-xs font-bold uppercase tracking-wider mb-4" style={{ color: textMuted }}>
              A aguardar revisão ({pendingPosts.length})
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {pendingPosts.map(post => renderPostCard(post, true))}
            </div>
          </section>
        )}

        {publishedPosts.length > 0 && (
          <section className="mb-12">
            <h2 className="text-xs font-bold uppercase tracking-wider mb-4" style={{ color: textMuted }}>
              Publicados ({publishedPosts.length})
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {publishedPosts.map(post => renderPostCard(post, false))}
            </div>
          </section>
        )}

        {/* Approved, not yet published */}
        {approvedPosts.length > 0 && (
          <section>
            <h2 className="text-xs font-bold uppercase tracking-wider mb-4" style={{ color: textMuted }}>
              Aprovados, a publicar ({approvedPosts.length})
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {approvedPosts.map(post => (
                <div
                  key={post.id}
                  className="rounded-xl overflow-hidden opacity-80"
                  style={{ backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.8)' }}
                >
                  {getCoverUrl(post) && (
                    <div className="aspect-video overflow-hidden">
                      <MediaPreview url={getCoverUrl(post)!} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="p-4 flex items-center gap-2">
                    <Check className="w-4 h-4 flex-shrink-0" style={{ color: primaryColor }} />
                    <span className="text-sm font-medium truncate">{post.title}</span>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {posts.length === 0 && (
          <div className="text-center py-16">
            <p className="text-lg font-medium" style={{ color: textMuted }}>
              Ainda não há conteúdo partilhado.
            </p>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="text-center py-6 px-4">
        <p className="text-[10px] uppercase tracking-widest font-medium" style={{ color: textMuted }}>
          Powered by MOHIT
        </p>
      </footer>

      {/* Changes Dialog */}
      {changesDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setChangesDialog(null)}>
          <div
            className="w-full max-w-md rounded-2xl p-6 shadow-2xl"
            style={{ backgroundColor: textColor === '#ffffff' ? '#1a1a1a' : '#ffffff' }}
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold" style={{ color: textColor === '#ffffff' ? '#fff' : '#111' }}>Pedir Alterações</h3>
              <button onClick={() => setChangesDialog(null)} className="p-1 rounded-lg hover:opacity-70">
                <X className="w-5 h-5" style={{ color: textMuted }} />
              </button>
            </div>
            <textarea
              className="w-full p-3 rounded-xl border text-sm resize-none"
              rows={4}
              placeholder="Descreve as alterações necessárias... *"
              value={changesComment}
              onChange={e => setChangesComment(e.target.value)}
              style={{ borderColor: accentColor + '40', color: textColor === '#ffffff' ? '#fff' : '#111', backgroundColor: textColor === '#ffffff' ? '#2a2a2a' : '#f9f9f9' }}
            />
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setChangesDialog(null)}
                className="px-4 py-2 rounded-full text-sm font-medium"
                style={{ color: textMuted }}
              >
                Cancelar
              </button>
              <button
                onClick={handleRequestChanges}
                disabled={!changesComment.trim() || submitting}
                className="px-5 py-2 rounded-full text-sm font-bold disabled:opacity-50 transition-transform hover:scale-[1.02]"
                style={{ backgroundColor: primaryColor, color: primaryTextColor }}
              >
                Enviar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Comment Dialog */}
      {commentDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setCommentDialog(null)}>
          <div
            className="w-full max-w-md rounded-2xl p-6 shadow-2xl"
            style={{ backgroundColor: textColor === '#ffffff' ? '#1a1a1a' : '#ffffff' }}
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold" style={{ color: textColor === '#ffffff' ? '#fff' : '#111' }}>Comentário</h3>
              <button onClick={() => setCommentDialog(null)} className="p-1 rounded-lg hover:opacity-70">
                <X className="w-5 h-5" style={{ color: textMuted }} />
              </button>
            </div>
            <MentionTextarea
              className="w-full p-3 rounded-xl border text-sm resize-none"
              rows={3}
              placeholder={mentionCandidates && mentionCandidates.length > 0
                ? 'Escreve o teu comentário... (@ para mencionar a equipa)'
                : 'Escreve o teu comentário...'}
              value={commentText}
              onChange={setCommentText}
              candidates={mentionCandidates || []}
              onSubmit={handleAddComment}
              autoFocus
              style={{ borderColor: accentColor + '40', color: textColor === '#ffffff' ? '#fff' : '#111', backgroundColor: textColor === '#ffffff' ? '#2a2a2a' : '#f9f9f9' }}
            />
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setCommentDialog(null)}
                className="px-4 py-2 rounded-full text-sm font-medium"
                style={{ color: textMuted }}
              >
                Cancelar
              </button>
              <button
                onClick={handleAddComment}
                disabled={!commentText.trim() || submitting}
                className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full text-sm font-bold disabled:opacity-50 transition-transform hover:scale-[1.02]"
                style={{ backgroundColor: primaryColor, color: primaryTextColor }}
              >
                <Send className="w-3.5 h-3.5" />
                Enviar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
